import { Popover } from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useEffect, useRef } from 'react';
import BadgeV3 from '../../badge/BadgeV3';
import { useFilterContext } from '../FilterV2Context';

export type BaseFilterParams = {
    id: string;
    label: string;
};

export type BaseFilterProps = BaseFilterParams & {
    children: ReactNode | ReactNode[];
    activeCount: number;
    onMenuClose: () => void;
};

function BaseFilter({ id, label, activeCount, children, onMenuClose }: BaseFilterProps): JSX.Element {
    const btnRef = useRef<HTMLButtonElement>(null);
    const ctx = useFilterContext();

    useEffect(() => {
        if (ctx.focusedFilter === id) {
            btnRef.current?.click();
        }
    }, [ctx.focusedFilter, id]);

    return (
        <Popover className="w-full">
            {({ open }): JSX.Element => {
                return (
                    <>
                        <Popover.Button
                            className={clsx(
                                'outline-none group flex items-center justify-between p-3 cursor-pointer w-full hover:bg-indigo-50 focus:bg-indigo-50',
                                open && 'bg-indigo-50 dark:bg-indigo-400/[0.15]'
                            )}
                            ref={btnRef}
                        >
                            <span className="text-gray-600 dark:text-gray-200 text-sm relative shrink-0 text-left pr-12">
                                {label}
                                {activeCount > 0 && (
                                    <span className=" ml-2 shrink-0 absolute">
                                        <BadgeV3 label={activeCount.toString()} color="indigo" rounded />
                                    </span>
                                )}
                            </span>

                            <span
                                className={clsx(
                                    'opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-200 ml-auto',
                                    open && 'opacity-100'
                                )}
                            >
                                <ChevronRightIcon className="text-indigo-600 h-4 w-4" />
                            </span>
                        </Popover.Button>
                        <AnimatePresence>
                            {open && (
                                <Popover.Panel
                                    static
                                    className="absolute top-0 left-full ml-2 bg-white shadow-md rounded-md min-w-[200px] w-max p-2  dark:border-white/[0.05] dark:text-gray-200 dark:bg-white/[0.05]"
                                    as={motion.div}
                                >
                                    {children}
                                </Popover.Panel>
                            )}
                        </AnimatePresence>
                    </>
                );
            }}
        </Popover>
    );
}

export default BaseFilter;
